import { HttpResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { forkJoin, from } from "rxjs";
import { map, mergeMap } from "rxjs/operators";

import { QueryStrings } from "src/app/models/emitter-events.models";
import { MissingInspection } from "src/app/models/inspection.model";
import { environment } from "src/environments/environment";
import { RequestService } from "../request/request.service";

@Injectable({
	providedIn: "root",
})
export class MissingInspectionsService {
	constructor(private requestService: RequestService) {}

	/**
	 *  Get Missing Inspection List
	 * @param companyId Company ID
	 * @param query Query params
	 * @param isOldMIR
	 * @returns Missing inspections list
	 */
	getMissingInspections(companyId: string, query: QueryStrings, isOldMIR: boolean) {
		const { sort, sortDir, page, perPage, missingReportFocus, ...remainingQueryStrings } = query;
		const url = `${environment.environmentConstants.APP_ENDPOINT_EVIR_V2}/inspections/missing?page=${page}&perPage=${perPage}&sort=${sort}&sortDir=${sortDir}`;
		const data = {
			companyId,
			...remainingQueryStrings,
			timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
			allChildren: true,
			missingReportFocus: isOldMIR && missingReportFocus === "gps" ? "gps-v1" : missingReportFocus,
		};

		return this.requestService.post<HttpResponse<MissingInspection[]>>({
			url,
			httpOptions: { observe: "response" },
			data,
		});
	}

	getAllMissingInspections(
		totalCount: number,
		chunkSize: number,
		perPage: number,
		companyId: string,
		queryStrings: QueryStrings,
		isOldMIR: boolean,
	) {
		const totalChunks = Math.ceil(totalCount / chunkSize);
		const pagesPerChunk = Math.floor(chunkSize / perPage);
		const totalPages = Math.ceil(totalCount / perPage);
		const chunkRanges = Array.from({ length: totalChunks }, (_, index) => {
			const startPage = index * pagesPerChunk + 1;
			const endPage = Math.min(startPage + pagesPerChunk - 1, totalPages);

			return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
		});

		return from(chunkRanges).pipe(
			mergeMap(pageNumbers => {
				const requests = pageNumbers.map(page =>
					this.getMissingInspections(companyId, { ...queryStrings, page: page.toString(), perPage: perPage.toString() }, isOldMIR)
				);

				return forkJoin(requests).pipe(
					map(responses => responses.map(({ body }) => body).flat()),
				);
			}, 1),
		);
	}
}
